import React from "react";
import { arrayOf, number, shape, string } from "prop-types";
import propsResolver from "@insightfulscience/shared-utils/fn/props-resolver";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import parseNumber from "@insightfulscience/shared-utils/parsers/parseNumber";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import routes, { toLoginUrl } from "@src/routes";
import SelectSeatsForm from "@src/SelectSeatsForm";
import SiteLayout from "@src/SiteLayout";
import withErrorHandling from "@src/AppError/withErrorHandlingHoC";
import WithServices from "@src/services";
import { isExpectedGqlError } from "@insightfulscience/shared-utils/gqlClient";
import { isLoaded } from "@insightfulscience/shared-utils/fn";
import { SUBSCRIPTION } from "@src/purchaseTypes";
import WithMaintenance from "@src/Maintenance/WithMaintenance";
import WithConditions from "@insightfulscience/smart-react/hocs/WithConditions";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import resources from "../../locales/select-seats-page";

const SelectNumberOfSeatsPage = props => {
	const [t] = useLocale("select-seats-page");
	return (
		<SiteLayout>
			<PageMeta title={t("PAGE_TITLE")}>
				<PageMeta.Robots content={t("META_ROBOTS_CONTENT")} />
			</PageMeta>
			<SimpleTemplate>
				<SelectSeatsForm {...props} />
			</SimpleTemplate>
		</SiteLayout>
	);
};

SelectNumberOfSeatsPage.propTypes = {
	priceList: arrayOf(
		shape({
			subscriptionSize: number.isRequired,
			price: number.isRequired,
			betterOption: number,
		}),
	),
	planCode: string,
	initialSeats: number,
};

const savePriceItemAndGoToBillingInfo = (
	{ planCode, priceList },
	context,
	{ storageService, authService },
) => {
	const { subscriptionSize: seats, price } = priceList[0];
	storageService.priceItem.write({
		planCode,
		seats,
		price,
		purchaseType: SUBSCRIPTION,
	});
	return authService.isAuthorized()
		? routes.billingInfo //
		: toLoginUrl({ returnUrl: routes.billingInfo });
};

SelectNumberOfSeatsPage.getInitialProps = propsResolver({
	priceList: ({ query }, { ecommerceService }) => ecommerceService.getPrices(query.planCode),
	planCode: ({ query }) => query.planCode,
	initialSeats: ({ query }) => parseNumber(query.seats),
});

SelectNumberOfSeatsPage.preConditions = [
	[({ query }) => !!query.planCode, routes.external.howToBuy],
];

SelectNumberOfSeatsPage.postConditions = [
	[isLoaded, routes.external.howToBuy],
	[({ priceList }) => priceList?.length > 0, routes.external.howToBuy],
	[({ priceList }) => priceList.length !== 1, savePriceItemAndGoToBillingInfo],
];

SelectNumberOfSeatsPage.onError = [[isExpectedGqlError(400, 404), routes.external.howToBuy]];

export default pipe(
	SelectNumberOfSeatsPage,
	WithConditions,
	WithLocale({ resources }),
	withErrorHandling,
	WithServices,
	WithMaintenance({ level: "NEW_ECOMM" }),
);
